import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useCallback, useMemo } from "react";
import { User } from "@common/types/user";
import { Picker } from "@design-system/Picker/Picker.lib";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { ApplyCloseTemplateEntityRow } from "../applyCloseTemplate.schema";
import type { ApplyCloseTemplateEntitySelectionRow } from "../applyCloseTemplateEntitySelection.types";
import { HStack } from "@design-system/HStack/HStack";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { filterOrganizationMembersByWorkspace } from "../applyCloseTemplateOrganizationMembers";
import { useTemplateApplication } from "../useTemplateApplication";

interface Props {
    row: CellContext<
        ViewTableFeatures,
        ApplyCloseTemplateEntitySelectionRow,
        string
    >["row"];
    field: "preparerId" | "reviewerId" | "managerId";
    addText: string;
    emptyLabel: string;
}

export const ApplyCloseEntityMemberCell = ({
    row,
    field,
    addText,
    emptyLabel,
}: Props) => {
    const { getEntityRow, setEntityRowField, members, getMemberLabel } =
        useTemplateApplication();
    const item = row.original;

    const idStr = item.entityIdStr;
    const rowState = idStr ? getEntityRow(idStr) : undefined;
    const memberId = (rowState?.[field] ?? null) as number | null;

    const membersForWorkspace = useMemo(
        () => filterOrganizationMembersByWorkspace(members, item.workspaceId),
        [members, item.workspaceId],
    );

    const selectedMember = useMemo(
        () =>
            memberId != null
                ? (membersForWorkspace.find((m) => m.user.id === memberId) ??
                  null)
                : null,
        [memberId, membersForWorkspace],
    );

    const validMemberId = selectedMember ? memberId : null;

    const handleChange = useCallback(
        (id: User["id"] | null) => {
            if (!idStr) {
                return;
            }
            if (id == null) {
                setEntityRowField(
                    idStr,
                    field,
                    null as ApplyCloseTemplateEntityRow[typeof field],
                );
                return;
            }
            const hasWorkspaceAccess = membersForWorkspace.some(
                (m) => m.user.id === id,
            );
            if (!hasWorkspaceAccess) {
                return;
            }
            setEntityRowField(
                idStr,
                field,
                id as ApplyCloseTemplateEntityRow[typeof field],
            );
        },
        [field, idStr, membersForWorkspace, setEntityRowField],
    );

    if (item.isClientRow || !idStr) {
        return null;
    }

    if (!row.original.isApplicationSelected || !rowState) {
        return (
            <ViewTableCell>
                <span className="text-muted">-</span>
            </ViewTableCell>
        );
    }

    return (
        <ViewTableCell>
            <Picker.Select<User["id"]>
                value={validMemberId}
                onValueChange={handleChange}
            >
                <Picker.CellContent
                    value={
                        selectedMember && (
                            <HStack gap={8} align="center">
                                <UserAvatar
                                    user={selectedMember.user}
                                    size="sm"
                                    aria-hidden
                                />
                                <BreakableText
                                    text={getMemberLabel(
                                        selectedMember.user.id,
                                    )}
                                    showTooltip
                                />
                            </HStack>
                        )
                    }
                    aria-label={
                        selectedMember
                            ? getMemberLabel(selectedMember.user.id)
                            : undefined
                    }
                    addText={addText}
                    hideAddIcon
                />
                <Picker.Popup>
                    <Picker.List>
                        <Picker.Item onSelect={() => handleChange(null)}>
                            {emptyLabel}
                        </Picker.Item>
                        {membersForWorkspace.map((member) => (
                            <Picker.Item
                                key={member.user.id}
                                value={member.user.id}
                                leading={
                                    <UserAvatar
                                        user={member.user}
                                        size="xs"
                                        aria-hidden
                                    />
                                }
                            >
                                {getMemberLabel(member.user.id)}
                            </Picker.Item>
                        ))}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
        </ViewTableCell>
    );
};
